import { useCallback, type RefObject } from 'react';
import type Konva from 'konva';
import { globalEventBus } from '../../../core/events/IEventBus';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT } from '../../../domains/media/constants/mediaReleaseEvents';
import { Tracker } from '../../../domains/analytics/services/Tracker';
import { MEDIA } from '../../../domains/analytics/constants/MetricEvents';
import { useDiagramStore } from '../../../stores/diagramStore';

export interface CanvasPoint {
  x: number;
  y: number;
}

export interface MarkPendingMediaNodeUploadedInput {
  nodeId: string;
  objectKey: string;
  diagramId: string;
  mediaKind: 'image' | 'pdf';
  uploadedAttrs: (objectKey: string) => Record<string, unknown>;
  applyNodeChange: (nodeId: string, change: { attrs: Record<string, unknown> }) => void;
  patchCreateShapeNodeAttrsInHistory: (nodeId: string, attrs: Record<string, unknown>) => void;
  emitMediaReferencesChanged: (eventName: string, payload: { diagramId: string; addedKeys: string[] }) => void;
  trackMediaAdded: (eventName: string, payload: { mediaKind: 'image' | 'pdf' }) => void;
}

export interface RemovePendingMediaNodeOnFailureInput {
  nodeId: string;
  getStoreState: () => {
    selectedIds: Set<string>;
    setSelected: (nextSelection: Set<string>) => void;
  };
  deleteNode: (nodeId: string) => void;
  warn?: (message?: unknown, ...optionalParams: unknown[]) => void;
}

export interface MediaNodeLike {
  id: string;
  type: string;
  attrs?: Record<string, unknown>;
}

export interface RemoveStaleUploadingMediaNodesInput {
  nodes: MediaNodeLike[];
  mediaTypes: ReadonlySet<string>;
  deleteNode: (nodeId: string) => void;
}

export function delay(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function markPendingMediaNodeUploaded(input: MarkPendingMediaNodeUploadedInput): void {
  const attrs = input.uploadedAttrs(input.objectKey);
  input.applyNodeChange(input.nodeId, { attrs });
  input.patchCreateShapeNodeAttrsInHistory(input.nodeId, attrs);
  input.emitMediaReferencesChanged(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, {
    diagramId: input.diagramId,
    addedKeys: [input.objectKey],
  });
  input.trackMediaAdded(MEDIA.CANVAS_IMAGE_ADDED, { mediaKind: input.mediaKind });
}

export function removePendingMediaNodeOnFailure(input: RemovePendingMediaNodeOnFailureInput): void {
  try {
    const store = input.getStoreState();
    const nextSelection = new Set(store.selectedIds);
    if (nextSelection.delete(input.nodeId)) {
      store.setSelected(nextSelection);
    }
  } catch (error) {
    const warn = input.warn ?? console.warn;
    warn('[useCanvasMediaInsertionInternals] Failed to clear pending node from selection', {
      nodeId: input.nodeId,
      error,
    });
  }
  input.deleteNode(input.nodeId);
}

export function removeStaleUploadingMediaNodes(input: RemoveStaleUploadingMediaNodesInput): string[] {
  const removedIds: string[] = [];
  for (const node of input.nodes) {
    if (!input.mediaTypes.has(node.type)) continue;
    const attrs = node.attrs ?? {};
    const isUploading = attrs.uploadState === 'uploading';
    const objectKey = typeof attrs.objectKey === 'string' ? attrs.objectKey.trim() : '';
    if (!isUploading || objectKey.length > 0) continue;
    input.deleteNode(node.id);
    removedIds.push(node.id);
  }
  return removedIds;
}

export function useCanvasMediaInsertionInternals(params: {
  commandManager: CommandManager;
  diagramId: string;
  mediaKind: 'image' | 'pdf';
  uploadedAttrs: (objectKey: string) => Record<string, unknown>;
  minFailedPlaceholderMs: number;
}) {
  const {
    commandManager,
    diagramId,
    mediaKind,
    uploadedAttrs,
    minFailedPlaceholderMs,
  } = params;

  const markPendingNodeUploaded = useCallback((nodeId: string, objectKey: string) => {
    const mutationManager = getNodeMutationManager();
    markPendingMediaNodeUploaded({
      nodeId,
      objectKey,
      diagramId,
      mediaKind,
      uploadedAttrs,
      applyNodeChange: mutationManager.applyNodeChange.bind(mutationManager),
      patchCreateShapeNodeAttrsInHistory: commandManager.patchCreateShapeNodeAttrsInHistory.bind(commandManager),
      emitMediaReferencesChanged: globalEventBus.emit.bind(globalEventBus),
      trackMediaAdded: Tracker.track.bind(Tracker),
    });
  }, [commandManager, diagramId, mediaKind, uploadedAttrs]);

  const removePendingNodeOnFailure = useCallback((nodeId: string) => {
    const mutationManager = getNodeMutationManager();
    removePendingMediaNodeOnFailure({
      nodeId,
      getStoreState: useDiagramStore.getState,
      deleteNode: mutationManager.deleteNode.bind(mutationManager),
    });
  }, []);

  const waitForMinFailedPlaceholderVisibility = useCallback(async (startedAt: number) => {
    const elapsed = Date.now() - startedAt;
    const remaining = minFailedPlaceholderMs - elapsed;
    if (remaining > 0) {
      await delay(remaining);
    }
  }, [minFailedPlaceholderMs]);

  const getViewportCenter = useCallback((stageRef: RefObject<Konva.Stage | null>): CanvasPoint | null => {
    const stage = stageRef.current;
    if (!stage) return null;
    const scaleX = stage.scaleX() || 1;
    const scaleY = stage.scaleY() || 1;
    const centerX = (stage.width() / 2 - stage.x()) / scaleX;
    const centerY = (stage.height() / 2 - stage.y()) / scaleY;
    return { x: centerX, y: centerY };
  }, []);

  return {
    markPendingNodeUploaded,
    removePendingNodeOnFailure,
    waitForMinFailedPlaceholderVisibility,
    getViewportCenter,
  };
}
